import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LiquidProgressBar from './LiquidProgressBar';
import { Flame, Timer, Sparkles, Snowflake, CupSoda, ShieldCheck, Lock } from 'lucide-react';
import coinSound from './sounds/coin.mp3';
import CutleryIcon from './icons/CutleryIcon';
import FootstepsIcon from './icons/FootstepsIcon';
import MoonIcon from './icons/MoonIcon';
import { buildPlayerStatsSlideDescriptors } from '../utils/playerStatsSlides';
import { useLocale } from '../context/LocaleContext';


const FLAME_SPECTRUM = [
  { hex: '#450a0a' }, //0-9
  { hex: '#b91c1c' }, //10-19
  { hex: '#ea580c' }, //20-29
  { hex: '#f59e0b' }, //30-39
  { hex: '#facc15' }, //40-49
  { hex: '#fef08a' }, // Changed to black for contrast on light yellow
  { hex: '#e0f2fe' }, //60-69
  { hex: '#67e8f9' }, //70-79
  { hex: '#3b82f6' }, //80-89
  { hex: '#6366f1' }, //90+
];

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

const PlayerStats = ({
  level,
  xp,
  xpToNextLevel,
  playerName,
  streak = 0,
  streakLastIncrementAt = null,
  aurasBalance = 0,
  aurasEffects = [],
  progressEvent = null,
  onProgressAnimationComplete,
  nutritionStats = null,
  fitnessStats = null,
  healthkitSlides = [],
}) => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const currentXP = xp;
  const maxXP = xpToNextLevel;
  const percentage = Math.round((currentXP / maxXP) * 100);
  const normalizedStreak = typeof streak === 'number' && streak > 0 ? streak : 0;
  const [isProgressAnimating, setIsProgressAnimating] = useState(false);
  const coinAudioRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const hasNutritionSlide = Boolean(nutritionStats);

  const resolvedFitnessStats = useMemo(() => {
    if (fitnessStats && typeof fitnessStats === 'object') {
      return fitnessStats;
    }
    return { status: 'unavailable', activeEnergyKcal: null, workoutTargetKcal: null, lastSyncedAt: null };
  }, [fitnessStats]);

  const slideDescriptors = useMemo(() => buildPlayerStatsSlideDescriptors({
    hasNutritionSlide,
    healthkitSlides,
  }), [hasNutritionSlide, healthkitSlides]);
  const slideCount = slideDescriptors.length;
  const activeSlide = slideDescriptors[currentSlide] || slideDescriptors[0];

  const formatTime = (dateLike) => {
    if (!dateLike) {
      return null;
    }
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const dotPatternStyle = {
    backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
    backgroundSize: '1rem 1rem',
  };

  const streakTheme = useMemo(() => {
    const bucket = normalizedStreak > 0 ? Math.min(Math.floor(normalizedStreak / 10), FLAME_SPECTRUM.length - 1) : 0;
    return FLAME_SPECTRUM[bucket];
  }, [normalizedStreak]);

  const flameColor = streakTheme.hex;

  // Refactored to return an object with time string and urgency boolean
  const streakWarning = useMemo(() => {
    if (!streakLastIncrementAt || normalizedStreak === 0) return null;

    const last = new Date(streakLastIncrementAt);
    if (Number.isNaN(last.getTime())) return null;

    const now = Date.now();
    const diff = now - last.getTime();
    const remaining = TWENTY_FOUR_HOURS_MS - diff;

    // Only show if within the 3 hour window (and not expired)
    if (remaining <= 0 || remaining > THREE_HOURS_MS) return null;

    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

    // Format: "2h 15m" or "45m"
    const timeString = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    // Urgency flag: true if less than 1 hour
    const isUrgent = hours === 0;

    return { timeString, isUrgent };
  }, [streakLastIncrementAt, normalizedStreak]);

  useEffect(() => {
    if (!progressEvent) return;
    setIsProgressAnimating(true);

    if (!coinAudioRef.current) {
      const audio = new Audio(coinSound);
      audio.preload = 'auto';
      audio.volume = 1.0;
      coinAudioRef.current = audio;
    }

    try {
      coinAudioRef.current.currentTime = 0;
      void coinAudioRef.current.play();
    } catch (err) {
      console.warn('[PlayerStats] Coin sound failed to play', err);
    }

    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try {
        navigator.vibrate([50]);
      } catch (err) {
        console.warn('[PlayerStats] Vibration failed', err);
      }
    }
  }, [progressEvent]);

  const handleProgressComplete = () => {
    setIsProgressAnimating(false);
    onProgressAnimationComplete?.();
  };

  const handleDragEnd = (event, info) => {
    const threshold = 50;
    if (info.offset.x < -threshold) {
      setCurrentSlide((prev) => Math.min(prev + 1, slideCount - 1));
    } else if (info.offset.x > threshold) {
      setCurrentSlide((prev) => Math.max(prev - 1, 0));
    }
  };

  const activeAurasEffects = useMemo(() => {
    const now = Date.now();
    return (aurasEffects || []).filter((eff) => eff?.expiresAt && new Date(eff.expiresAt).getTime() > now);
  }, [aurasEffects]);

  const effectIconMap = {
    icebath: Snowflake,
    preworkout: CupSoda,
    cheatmeal: ShieldCheck,
  };

  useEffect(() => {
    setCurrentSlide((prev) => Math.min(prev, Math.max(slideCount - 1, 0)));
  }, [slideCount]);

  const healthKitIconMap = {
    steps: FootstepsIcon,
    sleep: MoonIcon,
  };

  const renderHealthKitSlide = (slide) => {
    const Icon = healthKitIconMap[slide.key] || FootstepsIcon;
    const progressWidth = slide.isLockedTeaser ? 0 : `${slide.progressPercent}%`;
    const headerTone = slide.key === 'sleep'
      ? 'bg-indigo-500/20 text-indigo-300'
      : 'bg-cyan-500/20 text-cyan-300';
    const progressTone = slide.completed
      ? 'from-emerald-500 to-green-400'
      : slide.key === 'sleep'
        ? 'from-indigo-500 to-violet-400'
        : 'from-cyan-500 to-sky-400';

    return (
      <motion.div
        key={`healthkit-${slide.key}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.2 }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        onDragEnd={handleDragEnd}
        className="px-3 py-2 h-full flex flex-col justify-between pb-2"
      >
        <div className="justify-between">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-lg ${headerTone}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="font-bold text-white text-lg">{slide.title}</span>
            </div>

            <span className={`text-sm font-medium tracking-wide ${slide.isLockedTeaser ? 'text-gray-400 uppercase text-xs font-semibold' : 'text-white'}`}>
              {slide.valueLabel}
            </span>
          </div>

          <div className="space-y-2 w-full">
            <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden border border-white/5">
              {!slide.isLockedTeaser && (
                <motion.div
                  className={`absolute top-0 left-0 h-full bg-gradient-to-r ${progressTone}`}
                  initial={{ width: 0 }}
                  animate={{ width: progressWidth }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              )}
              {slide.isLockedTeaser && (
                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                  <Lock size={10} className="mr-1" />
                  {t('playerStats.locked', 'Locked')}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-[10px] uppercase font-bold text-gray-400">
              <span>{slide.statusText}</span>
              {!slide.isLockedTeaser && formatTime(slide.lastSyncedAt) ? (
                <span>{t('playerStats.syncedAt', 'Synced {time}', { time: formatTime(slide.lastSyncedAt) })}</span>
              ) : (
                <span />
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <section>
      <div className="bg-slate-900/40 backdrop-blur-2xl rounded-[1.5rem] shadow-xl relative overflow-hidden border border-white/10 h-32">
        {/* Soft glow effect */}
        <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl opacity-70 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={dotPatternStyle} />

        <div className="relative z-10 h-full">
          <AnimatePresence initial={false} mode="wait">
            {activeSlide?.kind === 'main' ? (
              <motion.div
                key="main-stats"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleDragEnd}
                className="px-3 py-2 h-full flex flex-col justify-between pb-2"
              >
                <div className='justify-between'>
                  {/* Header */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                  {/* Name / greeting */}
                  <div className="min-w-0 flex-1">
                    <h2 className="font-bold text-white text-lg truncate">
                      {t('playerStats.greeting', 'Hi {name}', { name: playerName })}
                    </h2>
                  </div>

                  {/* Right-side stats */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => navigate('/shop')}
                      className="flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/40 text-cyan-200 text-xs font-bold hover:bg-cyan-500/20 transition shrink-0"
                    >
                      <Sparkles size={14} className="shrink-0" />
                      <span className="whitespace-nowrap">
                        {aurasBalance} Auras
                      </span>
                    </button>

                    <div className="px-2 py-0.5 rounded bg-yellow-400/10 border border-yellow-400/20 shrink-0">
                      <p className="text-xs font-bold uppercase tracking-wider text-yellow-400 whitespace-nowrap">
                        {t('settings.levelBadge', 'Level {level}', { level })}
                      </p>
                    </div>
                  </div>
                </div>

                  {/* Progress Bar Section */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-end text-xs font-medium text-gray-400">
                      <span>{currentXP} <span className="text-gray-600">/</span> {maxXP} XP</span>
                      <span className="text-white">{percentage}%</span>
                    </div>
                    <motion.div
                      animate={{ scale: isProgressAnimating ? 1.05 : 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                    >
                      <LiquidProgressBar
                        value={currentXP}
                        max={maxXP}
                        progressEvent={progressEvent}
                        onAnimationComplete={handleProgressComplete}
                        heightClass="h-2"
                      />
                    </motion.div>

                    {/* Streak & Warning Row */}
                    <div className="flex items-center gap-2 pt-0.5 flex-wrap">
                      <div
                        className="flex items-center p-1 rounded-lg border bg-white/20 backdrop-blur-sm transition-colors duration-500"
                        style={{ borderColor: `${flameColor}40` }}
                      >
                        <Flame size={16} style={{ color: flameColor, fill: flameColor }} />
                        <span className="text-sm font-bold tracking-wide" style={{ color: streakTheme.hex }}>
                          {normalizedStreak}
                        </span>
                      </div>

                      {streakWarning && (
                        <div className={`
                        flex items-center gap-1.5 p-1 rounded-lg border backdrop-blur-sm animate-fade-in
                        ${streakWarning.isUrgent
                            ? 'bg-red-500/10 border-red-500/30 text-red-400'
                            : 'bg-orange-500/10 border-orange-500/30 text-orange-300'}
                      `}>
                          <Timer size={14} className={streakWarning.isUrgent ? 'animate-pulse' : ''} />
                          <span className="text-xs font-bold tracking-wide uppercase">
                            {t('playerStats.timeLeft', '{time} left', { time: streakWarning.timeString })}
                          </span>
                        </div>
                      )}

                      {activeAurasEffects.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {activeAurasEffects.map((eff) => {
                            const Icon = effectIconMap[eff.type] || Sparkles;
                            return (
                              <div key={eff.type} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-800/70 border border-slate-700 text-xs text-cyan-100">
                                <Icon size={14} className="text-cyan-300" />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </motion.div>


            ) : activeSlide?.kind === 'nutrition' ? (
              <motion.div
                key="nutrition-stats"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleDragEnd}
                className="px-3 py-2 h-full flex flex-col justify-between pb-2"
              >

                <div className='justify-between'>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-500/20 rounded-lg text-green-400">
                        <CutleryIcon className="w-5 h-5" />                      </div>
                      <span className="font-bold text-white text-lg">{t('playerStats.nutritionToday', 'Nutrition Today')}</span>
                    </div>
                    <span className="text-sm text-white font-medium tracking-wide">
                      {Math.round(nutritionStats.eaten.calories)} / {Math.round(nutritionStats.expected.calories)} kcal
                    </span>
                  </div>

                  <div className="space-y-2 w-full">
                    {/* Main Calorie Bar */}
                    <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden border border-white/5">
                      <motion.div
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-emerald-400"
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((nutritionStats.eaten.calories / (nutritionStats.expected.calories || 1)) * 100, 100)}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                      />
                    </div>

                    {/* Macros */}
                    <div className="grid grid-cols-3 gap-2">
                      {['protein', 'carbs', 'fat'].map((macro) => {
                        const eaten = nutritionStats.eaten[macro] || 0;
                        const expected = nutritionStats.expected[macro] || 1;
                        const pct = Math.min((eaten / expected) * 100, 100);
                        const color = macro === 'protein' ? 'bg-blue-500' : macro === 'carbs' ? 'bg-yellow-500' : 'bg-rose-500';
                        const macroLabels = {
                          protein: t('questDetails.protein', 'Protein'),
                          carbs: t('questDetails.carbs', 'Carbs'),
                          fat: t('questDetails.fat', 'Fat'),
                        };
                        const label = macroLabels[macro];

                        return (
                          <div key={macro} className="space-y-1">
                            <div className="flex justify-between text-[10px] uppercase font-bold text-gray-400">
                              <span>{label}</span>
                              <span>{Math.round(eaten)}g</span>
                            </div>
                            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                              <motion.div
                                className={`h-full ${color}`}
                                initial={{ width: 0 }}
                                animate={{ width: `${pct}%` }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : activeSlide?.kind === 'calories' ? (
              <motion.div
                key="fitness-stats"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleDragEnd}
                className="px-3 py-2 h-full flex flex-col justify-between pb-2"
              >
                <div className='justify-between'>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-orange-500/20 rounded-lg text-orange-300">
                        <Flame size={18} />
                      </div>
                      <span className="font-bold text-white text-lg">{t('playerStats.caloriesBurned', 'Calories Burned')}</span>
                    </div>

                    {typeof resolvedFitnessStats.activeEnergyKcal === 'number' ? (
                      <span className="text-sm text-white font-medium tracking-wide">
                        {resolvedFitnessStats.workoutTargetKcal
                          ? `${resolvedFitnessStats.activeEnergyKcal} / ${resolvedFitnessStats.workoutTargetKcal} kcal`
                          : `${resolvedFitnessStats.activeEnergyKcal} kcal`}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 font-semibold tracking-wide uppercase">
                        {t('playerStats.notSynced', 'Not synced')}
                      </span>
                    )}
                  </div>

                  {typeof resolvedFitnessStats.activeEnergyKcal === 'number' ? (
                    <div className="space-y-2 w-full">
                      {resolvedFitnessStats.workoutTargetKcal ? (
                        <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden border border-white/5">
                          <motion.div
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-orange-500 to-amber-400"
                            initial={{ width: 0 }}
                            animate={{
                              width: `${Math.min(
                                (resolvedFitnessStats.activeEnergyKcal / (resolvedFitnessStats.workoutTargetKcal || 1)) * 100,
                                100
                              )}%`,
                            }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                          />
                        </div>
                      ) : (
                        <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden border border-white/5">
                          <motion.div
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-orange-500 to-amber-400"
                            initial={{ width: 0 }}
                            animate={{
                              width: `${Math.min((resolvedFitnessStats.activeEnergyKcal / 1000) * 100, 100)}%`,
                            }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-between text-[10px] uppercase font-bold text-gray-400">
                        <span>
                          {resolvedFitnessStats.workoutTargetKcal
                            ? resolvedFitnessStats.activeEnergyKcal >= resolvedFitnessStats.workoutTargetKcal
                              ? t('playerStats.workoutVerified', 'Workout verified')
                              : t('playerStats.workoutInProgress', 'Workout in progress')
                            : t('playerStats.activeEnergyToday', 'Active energy today')}
                        </span>
                        {formatTime(resolvedFitnessStats.lastSyncedAt) ? (
                          <span>{t('playerStats.syncedAt', 'Synced {time}', { time: formatTime(resolvedFitnessStats.lastSyncedAt) })}</span>
                        ) : (
                          <span />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 w-full">
                      <div className="text-sm text-gray-300 leading-snug">
                        {t('playerStats.healthKitPrompt', 'Enable HealthKit verification in Settings on iOS to track today&apos;s steps, sleep, and active calories.')}
                      </div>
                      <div className="text-xs text-gray-400">
                        {t('playerStats.healthKitSyncHint', 'Sync happens on app open and when returning to the foreground (max every 15 minutes).')}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ) : renderHealthKitSlide(activeSlide?.data)}
          </AnimatePresence>

          {/* Pagination Dots */}
          {slideCount > 1 && (
            <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
              {slideDescriptors.map((slide, index) => (
                <motion.div
                  key={slide.key}
                  animate={{ opacity: currentSlide === index ? 1 : 0.3, scale: currentSlide === index ? 1.2 : 1 }}
                  className="w-1.5 h-1.5 rounded-full bg-white"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PlayerStats;
